import { HeadroomFinalResponse } from "@/graphql/types/graphql";
import { Segment, SegmentationMethod } from "../types/types";

export interface SegmentRange {
  range: string;
  segment: Segment | null;
  data?: HeadroomFinalResponse;
  segmentValue?: string;
  segmentIndex: number;
}

export interface HeadroomTableSegment {
  index: number;
  segmentType: string;
  method: SegmentationMethod;
  ranges: SegmentRange[];
}

export interface HeadroomCombinedRow {
  manualRange: string;
  dataValue: string;
  data?: HeadroomFinalResponse;
}

export const sortHeadroomRows = (
  rows: SegmentRange[][],
  sortColumn: string,
  sortDirection: "asc" | "desc",
  isDataOnly: boolean
): SegmentRange[][] => {
  return rows.sort((a, b) => {
    const aData = a.find((r) => r.data)?.data;
    const bData = b.find((r) => r.data)?.data;
    if (sortColumn.startsWith("range") || sortColumn.startsWith("segmentValue")) {
      const segmentIndex = parseInt(sortColumn.match(/\d+/)?.[0] || "1") - 1;
      const aValue = a[segmentIndex]?.segmentValue || a[segmentIndex]?.range || "";
      const bValue = b[segmentIndex]?.segmentValue || b[segmentIndex]?.range || "";
      const isNumeric = (value: string) => !isNaN(parseFloat(value.split("-")[0]));
      if (isDataOnly && !isNumeric(aValue)) {
        return sortDirection === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      const parseRange = (range: string) => {
        const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
        return match ? parseFloat(match[1] || match[2]) : 0;
      };
      const aNum = parseRange(aValue);
      const bNum = parseRange(bValue);
      return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
    } else if (sortColumn === "totalCustomers") {
      return sortDirection === "asc"
        ? (Number(aData?.totalCustomers) || 0) - (Number(bData?.totalCustomers) || 0)
        : (Number(bData?.totalCustomers) || 0) - (Number(aData?.totalCustomers) || 0);
    } else if (sortColumn === "totalProspects") {
      return sortDirection === "asc"
        ? (Number(aData?.totalProspects) || 0) - (Number(bData?.totalProspects) || 0)
        : (Number(bData?.totalProspects) || 0) - (Number(aData?.totalProspects) || 0);
    } else if (sortColumn === "penetration") {
      return sortDirection === "asc"
        ? (Number(aData?.penetration) || 0) - (Number(bData?.penetration) || 0)
        : (Number(bData?.penetration) || 0) - (Number(aData?.penetration) || 0);
    } else if (sortColumn === "driverInitial") {
      return sortDirection === "asc"
        ? (Number(aData?.driverInitial) || 0) - (Number(bData?.driverInitial) || 0)
        : (Number(bData?.driverInitial) || 0) - (Number(aData?.driverInitial) || 0);
    } else if (sortColumn === "driverFinal") {
      return sortDirection === "asc"
        ? (Number(aData?.driverFinal) || 0) - (Number(bData?.driverFinal) || 0)
        : (Number(bData?.driverFinal) || 0) - (Number(aData?.driverFinal) || 0);
    } else if (sortColumn === "headroom") {
      return sortDirection === "asc"
        ? (Number(aData?.headroom) || 0) - (Number(bData?.headroom) || 0)
        : (Number(bData?.headroom) || 0) - (Number(aData?.headroom) || 0);
    } else if (sortColumn === "totalCustomersAfter") {
      return sortDirection === "asc"
        ? (Number(aData?.totalCustomersAfter) || 0) - (Number(bData?.totalCustomersAfter) || 0)
        : (Number(bData?.totalCustomersAfter) || 0) - (Number(aData?.totalCustomersAfter) || 0);
    } else if (sortColumn === "totalDriverInitial") {
      return sortDirection === "asc"
        ? (Number(aData?.totalDriverInitial) || 0) - (Number(bData?.totalDriverInitial) || 0)
        : (Number(bData?.totalDriverInitial) || 0) - (Number(aData?.totalDriverInitial) || 0);
    } else if (sortColumn === "totalDriverFinal") {
      return sortDirection === "asc"
        ? (Number(aData?.totalDriverFinal) || 0) - (Number(bData?.totalDriverFinal) || 0)
        : (Number(bData?.totalDriverFinal) || 0) - (Number(aData?.totalDriverFinal) || 0);
    } else if (sortColumn === "totalHeadroom") {
      return sortDirection === "asc"
        ? (Number(aData?.totalHeadroom) || 0) - (Number(bData?.totalHeadroom) || 0)
        : (Number(bData?.totalHeadroom) || 0) - (Number(aData?.totalHeadroom) || 0);
    } else if (sortColumn === "increasedPenetration") {
      return sortDirection === "asc"
        ? (Number(aData?.increasedPenetration) || 0) - (Number(bData?.increasedPenetration) || 0)
        : (Number(bData?.increasedPenetration) || 0) - (Number(aData?.increasedPenetration) || 0);
    } else if (sortColumn === "headroomPercent") {
      return sortDirection === "asc"
        ? (Number(aData?.headroomPercent) || 0) - (Number(bData?.headroomPercent) || 0)
        : (Number(bData?.headroomPercent) || 0) - (Number(aData?.headroomPercent) || 0);
    }
    return 0;
  });
};

export const sortHeadroomCombinedRows = (
  rows: HeadroomCombinedRow[],
  sortColumn: string,
  sortDirection: "asc" | "desc"
): HeadroomCombinedRow[] => {
  return rows.sort((a, b) => {
    if (sortColumn === "range") {
      const parseRange = (range: string) => {
        const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
        return match ? parseFloat(match[1] || match[2]) : 0;
      };
      const aNum = parseRange(a.manualRange || a.dataValue);
      const bNum = parseRange(b.manualRange || b.dataValue);
      return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
    } else if (sortColumn === "segmentValue") {
      const isNumeric = (value: string) => !isNaN(parseFloat(value.split("-")[0]));
      if (!isNumeric(a.dataValue)) {
        return sortDirection === "asc"
          ? a.dataValue.localeCompare(b.dataValue)
          : b.dataValue.localeCompare(a.dataValue);
      }
      const parseRange = (range: string) => {
        const match = range.match(/^>=(\d*\.?\d+)|(\d*\.?\d+)/);
        return match ? parseFloat(match[1] || match[2]) : 0;
      };
      const aNum = parseRange(a.dataValue);
      const bNum = parseRange(b.dataValue);
      return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
    } else if (sortColumn === "totalCustomers") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalCustomers) || 0) - (Number(b.data?.totalCustomers) || 0)
        : (Number(b.data?.totalCustomers) || 0) - (Number(a.data?.totalCustomers) || 0);
    } else if (sortColumn === "totalProspects") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalProspects) || 0) - (Number(b.data?.totalProspects) || 0)
        : (Number(b.data?.totalProspects) || 0) - (Number(a.data?.totalProspects) || 0);
    } else if (sortColumn === "penetration") {
      return sortDirection === "asc"
        ? (Number(a.data?.penetration) || 0) - (Number(b.data?.penetration) || 0)
        : (Number(b.data?.penetration) || 0) - (Number(a.data?.penetration) || 0);
    } else if (sortColumn === "driverInitial") {
      return sortDirection === "asc"
        ? (Number(a.data?.driverInitial) || 0) - (Number(b.data?.driverInitial) || 0)
        : (Number(b.data?.driverInitial) || 0) - (Number(a.data?.driverInitial) || 0);
    } else if (sortColumn === "driverFinal") {
      return sortDirection === "asc"
        ? (Number(a.data?.driverFinal) || 0) - (Number(b.data?.driverFinal) || 0)
        : (Number(b.data?.driverFinal) || 0) - (Number(a.data?.driverFinal) || 0);
    } else if (sortColumn === "headroom") {
      return sortDirection === "asc"
        ? (Number(a.data?.headroom) || 0) - (Number(b.data?.headroom) || 0)
        : (Number(b.data?.headroom) || 0) - (Number(a.data?.headroom) || 0);
    } else if (sortColumn === "totalCustomersAfter") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalCustomersAfter) || 0) - (Number(b.data?.totalCustomersAfter) || 0)
        : (Number(b.data?.totalCustomersAfter) || 0) - (Number(a.data?.totalCustomersAfter) || 0);
    } else if (sortColumn === "totalDriverInitial") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalDriverInitial) || 0) - (Number(b.data?.totalDriverInitial) || 0)
        : (Number(b.data?.totalDriverInitial) || 0) - (Number(a.data?.totalDriverInitial) || 0);
    } else if (sortColumn === "totalDriverFinal") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalDriverFinal) || 0) - (Number(b.data?.totalDriverFinal) || 0)
        : (Number(b.data?.totalDriverFinal) || 0) - (Number(a.data?.totalDriverFinal) || 0);
    } else if (sortColumn === "totalHeadroom") {
      return sortDirection === "asc"
        ? (Number(a.data?.totalHeadroom) || 0) - (Number(b.data?.totalHeadroom) || 0)
        : (Number(b.data?.totalHeadroom) || 0) - (Number(a.data?.totalHeadroom) || 0);
    } else if (sortColumn === "increasedPenetration") {
      return sortDirection === "asc"
        ? (Number(a.data?.increasedPenetration) || 0) - (Number(b.data?.increasedPenetration) || 0)
        : (Number(b.data?.increasedPenetration) || 0) - (Number(a.data?.increasedPenetration) || 0);
    } else if (sortColumn === "headroomPercent") {
      return sortDirection === "asc"
        ? (Number(a.data?.headroomPercent) || 0) - (Number(b.data?.headroomPercent) || 0)
        : (Number(b.data?.headroomPercent) || 0) - (Number(a.data?.headroomPercent) || 0);
    }
    return 0;
  });
};

export const formatHeadroomCombinedTableData = (
  segments: HeadroomTableSegment[],
  data: HeadroomFinalResponse[] | undefined
): {
  manualSegment: HeadroomTableSegment | undefined;
  dataSegment: HeadroomTableSegment | undefined;
  combinedRows: HeadroomCombinedRow[];
} => {
  const sortedSegments = [...segments].sort((a, b) => a.segmentType.localeCompare(b.segmentType));
  const manualSegment = sortedSegments.find((seg) => seg.method === "MANUAL");
  const dataSegment = sortedSegments.find((seg) => seg.method === "DATA");

  if (!data || data.length === 0 || !manualSegment || !dataSegment) {
    return { manualSegment, dataSegment, combinedRows: [] };
  }

  const primaryType = data[0]?.segment;
  const isManualPrimary = primaryType === manualSegment.segmentType;

  const seenCombinations = new Set<string>();
  const combinedRows = data
    .filter((d) => {
      const combinationKey = `${d.segmentValue}_${d.segmentRange || "null"}`;
      if (seenCombinations.has(combinationKey)) return false;
      seenCombinations.add(combinationKey);
      return true;
    })
    .map((d) => ({
      manualRange: isManualPrimary ? d.segmentValue : d.segmentRange || "",
      dataValue: isManualPrimary ? d.segmentRange || "" : d.segmentValue,
      data: d,
    }));

  return { manualSegment, dataSegment, combinedRows };
};

export const formatHeadroomTableData = (
  segments: HeadroomTableSegment[],
  data: HeadroomFinalResponse[] | undefined
): { displaySegments: HeadroomTableSegment[]; rows: SegmentRange[][]; isDataOnly: boolean } => {
  const displaySegments = [...segments].sort((a, b) => a.segmentType.localeCompare(b.segmentType));
  const isDataOnly = displaySegments.every((seg) => seg.method === "DATA");
  const isManualOnly = displaySegments.every((seg) => seg.method === "MANUAL") && segments.length === 1;
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (isTwoSameMethod) {
    const [firstSegment, secondSegment] = displaySegments;
    const rows: SegmentRange[][] = [];
    const seenCombinations = new Set<string>();
    firstSegment.ranges.forEach((firstRange) => {
      secondSegment.ranges.forEach((secondRange) => {
        const combinationKey = `${firstRange.segmentValue || firstRange.range}_${
          secondRange.segmentValue || secondRange.range
        }`;
        if (!seenCombinations.has(combinationKey)) {
          seenCombinations.add(combinationKey);
          const matchingData = data?.find(
            (d) =>
              d.segment === firstSegment.segmentType &&
              d.segmentValue === (firstRange.segmentValue || firstRange.range) &&
              d.segmentRange === (secondRange.segmentValue || secondRange.range)
          );
          rows.push([
            {
              range: firstRange.segmentValue || firstRange.range,
              segment: firstRange.segment,
              segmentValue: firstRange.segmentValue || firstRange.range,
              data: matchingData,
              segmentIndex: firstSegment.index,
            },
            {
              range: secondRange.segmentValue || secondRange.range,
              segment: secondRange.segment,
              segmentValue: secondRange.segmentValue || secondRange.range,
              data: undefined,
              segmentIndex: secondSegment.index,
            },
          ]);
        }
      });
    });
    return { displaySegments, rows, isDataOnly };
  }

  const rows: SegmentRange[][] = [];
  const seenValues = new Set<string>();
  displaySegments[0]?.ranges?.forEach((range) => {
    const value = range.segmentValue || range.range;
    if (!seenValues.has(value)) {
      seenValues.add(value);
      rows.push([
        {
          range: range.range,
          segment: range.segment,
          segmentValue: range.segmentValue || range.range,
          data: range.data,
          segmentIndex: displaySegments[0].index,
        },
      ]);
    }
  });

  return { displaySegments, rows, isDataOnly };
};
